import {
  Card,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import { formatCurrency } from "@/helpers/currency-helpers";
import { getDateRange } from "@/helpers/date-helpers";
import useGetBestsellingProducts from "@/hooks/requests/dashboard/sales/use-get-best-selling-products";
import { SalesDashboardMessages } from "@/messages/salesDashboard/sales-dashboard.messages";
import { DateRange } from "@/types/date.types";

type BestsellingProductsCardProps = {
  messages: SalesDashboardMessages;
};

export default function BestsellingProductsCard({ messages }: BestsellingProductsCardProps) {
  const { startDate, endDate } = getDateRange(DateRange.CURRENT_MONTH);
  const { error, data, isLoading } = useGetBestsellingProducts({ startDate, endDate });

  const shouldLoadError = !data && !!error && !isLoading;
  const noData = !data || data.length === 0;

  return (
    <Card sx={{ height: "100%", overflowY: "scroll" }}>
      <MDBox p={2} display="flex" flexDirection="column">
        <MDTypography variant="h6" fontWeight={"bold"}>
          {messages.bestSellingProductsTitle}
        </MDTypography>
        {isLoading ? (
          <>
            <Skeleton variant="text" width="50%" height={30} />
            <Skeleton variant="rectangular" width="100%" height={40} sx={{ mt: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2 }} />
          </>
        ) : !noData ? (
          <TableContainer sx={{ height: "100%", boxShadow: "none" }}>
            <Table aria-label="monthly attendants table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="regular"
                      textTransform="capitalize"
                    >
                      {messages.productColumn}
                    </MDTypography>
                  </TableCell>
                  <TableCell align="right">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="regular"
                      textTransform="capitalize"
                    >
                      {messages.valueColumn}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((product) => (
                  <TableRow key={product.code}>
                    <TableCell>
                      <MDTypography variant="button" fontWeight="bold" color={"text"}>
                        {product.name}
                      </MDTypography>
                      <MDBox
                        marginRight={"3px"}
                        display="flex"
                        gap={"3px"}
                        color="success"
                        fontWeight="bold"
                      >
                        <MDTypography variant="button" fontWeight="bold" color={"success"}>
                          {product.countOrders}
                        </MDTypography>
                        <MDTypography variant="button" color="text">
                          {product.countOrders > 1 ? messages.ordersLabel : messages.orderLabel}
                        </MDTypography>
                      </MDBox>
                    </TableCell>
                    <TableCell align="right">
                      <MDTypography variant="button" color="text" fontWeight="medium">
                        {formatCurrency(product.totalValue)}
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : shouldLoadError ? (
          <AlertBlock
            title={messages.errorToLoad}
            description={error?.message || ""}
            alt={messages.notFoundAlt}
          />
        ) : (
          <AlertBlock
            title={messages.noDataTitle}
            description={messages.noDataMessage}
            alt={messages.notFoundAlt}
          />
        )}
      </MDBox>
    </Card>
  );
}
